import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router';
import { useAuth } from '../hooks/useAuth';
import { workspacesApi, accountsApi } from '../lib/api';
import { Spinner, Alert, EmptyState } from '@rankhelm/ui';
import type { Workspace, Account } from '@rankhelm/shared';
import { workspaceTitles } from '../lib/workspace';
import { WorkspaceThumbnail } from '../components/WorkspaceThumbnail';

export function WorkspacesPage() {
  const { user } = useAuth();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = user?.role === 'account_admin' || isSuperAdmin;

  // Filter
  const [filterAccountId, setFilterAccountId] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const wsPromise = workspacesApi.list();
      const acctPromise = isSuperAdmin
        ? accountsApi.list().catch(() => ({ data: [] as Account[] }))
        : null;

      const [wsRes, acctRes] = await Promise.all([wsPromise, acctPromise]);

      setWorkspaces((wsRes.data as Workspace[]) || []);
      if (acctRes) {
        const accountData = acctRes.data;
        setAccounts(Array.isArray(accountData) ? accountData as Account[] : accountData ? [accountData as Account] : []);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load workspaces');
    } finally {
      setLoading(false);
    }
  }

  const accountMap = useMemo(() => {
    const map: Record<string, string> = {};
    accounts.forEach((a) => { map[a.id] = a.name; });
    return map;
  }, [accounts]);

  const filteredWorkspaces = useMemo(() => {
    if (!isSuperAdmin || filterAccountId === 'all') return workspaces;
    return workspaces.filter((w) => w.account_id === filterAccountId);
  }, [workspaces, filterAccountId, isSuperAdmin]);

  if (loading) return <Spinner message="Loading workspaces..." fullPage />;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-white">Workspaces</h1>
        {isAdmin && !isSuperAdmin && (
          <Link
            to="/workspaces/new"
            className="px-4 py-2 bg-helm-500 hover:bg-helm-400 text-white text-sm font-medium rounded-lg transition-colors"
          >
            + New Workspace
          </Link>
        )}
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {/* Filter (super admin only) */}
      {isSuperAdmin && workspaces.length > 0 && (
        <div className="flex items-center gap-3 mb-4">
          <select
            value={filterAccountId}
            onChange={(e) => setFilterAccountId(e.target.value)}
            aria-label="Filter by account"
            className="px-3 py-2 bg-navy-900 border border-navy-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-helm-500"
          >
            <option value="all">All Accounts</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
          <span className="text-xs text-slate-500">
            {filteredWorkspaces.length} workspace{filteredWorkspaces.length !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* Workspace List */}
      {filteredWorkspaces.length === 0 ? (
        <EmptyState message="No workspaces yet. Create your first workspace to get started." />
      ) : (
        <div className="space-y-3">
          {filteredWorkspaces.map((workspace) => {
            const titles = workspaceTitles(workspace);
            return (
            <Link
              key={workspace.id}
              to={`/workspaces/${workspace.id}`}
              className="block bg-navy-900 border border-navy-700 hover:border-navy-600 rounded-xl p-5 transition-colors"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-4 min-w-0">
                  <WorkspaceThumbnail
                    workspaceId={workspace.id}
                    websiteUrl={workspace.website_url}
                    alt={`${titles.primary} screenshot`}
                    className="w-24 h-[72px]"
                  />
                  <div className="min-w-0">
                    <p className="text-white font-semibold text-lg truncate">{titles.primary}</p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5">
                      {titles.secondary && (
                        <p className="text-sm text-slate-400 truncate">{titles.secondary}</p>
                      )}
                      {workspace.display_id && (
                        <span className="px-2 py-0.5 bg-navy-700 text-slate-300 text-xs font-medium rounded font-mono">
                          {workspace.display_id}
                        </span>
                      )}
                      {isSuperAdmin && workspace.account_id && (
                        <span className="px-2 py-0.5 bg-navy-700 text-slate-300 text-xs font-medium rounded">
                          {accountMap[workspace.account_id] || 'Unknown account'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <span className="px-4 py-2 bg-helm-500/15 hover:bg-helm-500/25 text-helm-400 text-sm font-medium rounded-lg transition-colors">
                  Open
                </span>
              </div>
            </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
